/* antarti — motion core. Loaded with `defer` after the vendored GSAP, ScrollTrigger,
   SplitText and Lenis builds on every page except the password page.

   Responsibilities:
   1. Lenis smooth scrolling on fine-pointer devices, driven from GSAP's ticker and
      kept in sync with ScrollTrigger. Touch devices keep native scrolling.
   2. Scroll reveals for [data-reveal="fade | lines | unmask | stagger"]. Only elements
      below the first viewport are ever hidden; anything already on screen at load just
      shows, so the LCP element is never delayed and nothing flashes.
   3. Scroll lock while a <dialog data-scroll-lock> is open.
   4. Theme-editor and back/forward-cache hooks so triggers never go stale.

   Everything is off under prefers-reduced-motion, in the theme editor, and when the
   vendor scripts failed to load. */

(() => {
  'use strict';

  const A = (window.antarti = window.antarti || {});
  const cfg = A.config || {};
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const hasGsap = typeof window.gsap !== 'undefined';
  const gsap = window.gsap;
  const ST = window.ScrollTrigger;
  const Split = window.SplitText;
  const scale = cfg.motionIntensity === 'subtle' ? 0.5 : 1;
  const motionOff = () => reduce.matches || cfg.designMode || !hasGsap;

  if (hasGsap) {
    gsap.registerPlugin(...[ST, Split].filter(Boolean));
    if (ST) ST.config({ ignoreMobileResize: true });
  }

  /* ------------------------------------------------------------------ */
  /* Lenis                                                               */
  /* ------------------------------------------------------------------ */
  let lenis = null;
  let lenisTick = null;

  /* Lenis's anchor handling ignores CSS scroll-padding, so hand it the same offset. */
  const anchorOffset = () => -(parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0);

  function startLenis() {
    if (lenis || !cfg.smoothScroll || motionOff() || !finePointer.matches || typeof window.Lenis === 'undefined') return;
    lenis = new window.Lenis({
      lerp: 0.1,
      smoothWheel: true,
      syncTouch: false,
      autoRaf: false,
      anchors: { offset: anchorOffset() },
    });
    if (ST) lenis.on('scroll', ST.update);
    lenisTick = (t) => {
      if (lenis) lenis.raf(t * 1000);
    };
    gsap.ticker.add(lenisTick);
    gsap.ticker.lagSmoothing(0);
    A.lenis = lenis;
  }

  function stopLenis() {
    if (!lenis) return;
    if (lenisTick) gsap.ticker.remove(lenisTick);
    lenisTick = null;
    gsap.ticker.lagSmoothing(500, 33); // GSAP's default, only changed while Lenis drove the clock
    lenis.destroy();
    lenis = null;
    A.lenis = null;
  }

  A.lock = () => lenis && lenis.stop();
  A.unlock = () => lenis && lenis.start();
  document.addEventListener('antarti:lock', A.lock);
  document.addEventListener('antarti:unlock', A.unlock);

  /* Any <dialog data-scroll-lock> pauses smooth scrolling while open. The dialog's own
     content must stay scrollable, so it is also marked data-lenis-prevent. */
  const dialogObserver = new MutationObserver((records) => {
    for (const r of records) {
      if (r.target.open) A.lock();
      else A.unlock();
    }
  });

  function watchDialogs(root = document) {
    root.querySelectorAll('dialog[data-scroll-lock]').forEach((d) => {
      d.setAttribute('data-lenis-prevent', '');
      dialogObserver.observe(d, { attributes: true, attributeFilter: ['open'] });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Reveals                                                             */
  /* ------------------------------------------------------------------ */
  const splits = new WeakMap();

  const animations = {
    fade(el, delay) {
      return gsap.fromTo(
        el,
        { autoAlpha: 0, y: 14 * scale },
        { autoAlpha: 1, y: 0, duration: 0.7, ease: 'expo.out', delay, clearProps: 'transform' }
      );
    },

    stagger(el, delay) {
      const kids = Array.from(el.children);
      gsap.set(el, { autoAlpha: 1 });
      return gsap.fromTo(
        kids,
        { autoAlpha: 0, y: 18 * scale },
        { autoAlpha: 1, y: 0, duration: 0.7, ease: 'expo.out', delay, stagger: 0.08, clearProps: 'transform' }
      );
    },

    unmask(el, delay) {
      const media = el.querySelector('img, video, picture');
      const tl = gsap.timeline({ delay });
      tl.fromTo(
        el,
        { autoAlpha: 1, clipPath: 'inset(0 0 100% 0)' },
        { clipPath: 'inset(0 0 0% 0)', duration: 1.2, ease: 'expo.out', clearProps: 'clipPath' }
      );
      if (media && scale > 0) {
        tl.fromTo(media, { scale: 1.08 }, { scale: 1, duration: 1.4, ease: 'expo.out', clearProps: 'transform' }, 0);
      }
      return tl;
    },

    lines(el, delay) {
      if (!Split) return animations.fade(el, delay);
      gsap.set(el, { autoAlpha: 1 });
      const split = Split.create(el, {
        type: 'lines',
        mask: 'lines',
        autoSplit: true,
        linesClass: 'split-line',
        /* Headings can carry an accessible name; paragraphs and generic elements cannot,
           so there the line divs stay readable in DOM order instead. */
        aria: /^H[1-6]$/.test(el.tagName) ? 'auto' : 'none',
        onSplit(self) {
          return gsap.from(self.lines, { yPercent: 110, duration: 0.9, ease: 'expo.out', stagger: 0.08, delay });
        },
      });
      splits.set(el, split);
      return split;
    },
  };

  function show(el) {
    el.classList.remove('is-pending');
    el.dataset.revealed = '1';
  }

  function play(el) {
    if (el.dataset.revealed) return;
    el.dataset.revealed = '1';
    const type = el.dataset.reveal || 'fade';
    const run = animations[type] || animations.fade;
    const delay = parseFloat(el.dataset.revealDelay || '0') || 0;
    const start = () => {
      el.classList.remove('is-pending');
      run(el, delay);
    };
    if (type === 'lines' && document.fonts && document.fonts.ready) document.fonts.ready.then(start);
    else start();
  }

  /** Reverts a SplitText line reveal so the element's markup and aria are restored. */
  function unsplit(el) {
    const s = splits.get(el);
    if (s) {
      s.revert();
      splits.delete(el);
    }
  }

  const io =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) =>
            entries.forEach((e) => {
              if (e.isIntersecting) {
                play(e.target);
                io.unobserve(e.target);
              }
            }),
          { rootMargin: '0px 0px -12% 0px' }
        )
      : null;

  /**
   * Arms every un-revealed [data-reveal] under `root`.
   * Anything intersecting the first viewport is shown immediately, without animation.
   * @param {ParentNode} root
   * @param {{immediate?: boolean}} [opts] immediate: show everything now (theme editor reloads)
   */
  function reveal(root = document, opts = {}) {
    const els = Array.from(root.querySelectorAll('[data-reveal]')).filter((el) => !el.dataset.revealed);
    if (opts.immediate || motionOff()) {
      els.forEach(show);
      return;
    }
    const fold = window.innerHeight;
    els.forEach((el) => {
      if (el.getBoundingClientRect().top < fold) {
        show(el);
        return;
      }
      el.classList.add('is-pending');
      if (ST) ST.create({ trigger: el, start: 'top 88%', once: true, onEnter: () => play(el) });
      else if (io) io.observe(el);
      else show(el);
    });
  }

  function refresh() {
    if (ST) ST.refresh(true);
    if (lenis) lenis.resize();
  }

  /* ------------------------------------------------------------------ */
  /* Lifecycle                                                           */
  /* ------------------------------------------------------------------ */
  A.motion = {
    reveal,
    play,
    unsplit,
    refresh,
    get lenis() {
      return lenis;
    },
    scale,
    reduce,
  };

  startLenis();
  watchDialogs();
  reveal();

  reduce.addEventListener('change', () => {
    if (reduce.matches) {
      stopLenis();
      document.querySelectorAll('[data-reveal].is-pending').forEach(show);
    } else {
      startLenis();
    }
  });

  /* Theme editor: a re-rendered section arrives with fresh markup. */
  document.addEventListener('shopify:section:load', (e) => {
    reveal(e.target, { immediate: true });
    watchDialogs(e.target);
    refresh();
  });
  document.addEventListener('shopify:section:unload', (e) => {
    if (ST) ST.getAll().forEach((t) => t.trigger && e.target.contains(t.trigger) && t.kill());
  });

  /* Back/forward cache restores the page mid-scroll (theme.js re-dispatches pageshow
     as antarti:restore). ScrollTrigger handles the normal load/resize refreshes itself. */
  document.addEventListener('antarti:restore', refresh);
})();
